/**
 * Shared Axios client for investors.nurev.io. Mounts the investor's
 * JWT (role='shareholder' or 'admin' — both authorized for the
 * shareholder portal endpoints) on every request. On 401, clears the
 * session and the auth guard sends the user back to /login.
 *
 * All shareholder-portal data flows over `/shareholder-portal/*` on
 * the shared nurev-api backend. Auth flows use `/auth/accept-invite`
 * and `/auth/login` (legacy flow, no new backend code needed).
 */
import axios, { AxiosError } from 'axios';
import { useAuthStore } from '@/store/authStore';

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://api.app.nurev.io';

export const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' }
});

client.interceptors.request.use(cfg => {
  const t = useAuthStore.getState().token;
  if (t && cfg.headers) {
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

client.interceptors.response.use(
  r => r,
  (err: AxiosError) => {
    // On 401, drop the session — the auth guard will redirect to /login.
    if (err.response?.status === 401) {
      useAuthStore.getState().clear();
    }
    return Promise.reject(err);
  }
);

/**
 * Defensively unwrap an API response. nurev-api is inconsistent here:
 *
 *   - Most endpoints (the modern ones) return `{ success, data: T }`,
 *     in which case we return the inner `data`.
 *   - A few legacy endpoints (notably `/login` and `POST /accept-invite`)
 *     return a flat `T` (e.g. `{ token, user }`) at the top level — no
 *     envelope. For those, we just return the body as-is.
 *
 * The original version of this helper assumed `r.data.data` always existed
 * and silently returned `undefined` for flat-shape responses, breaking
 * login + accept-invite end-to-end (root cause of the 2026-05-28
 * shareholder portal incident). Don't reintroduce that.
 */
export async function unwrap<T>(p: Promise<{ data: { success?: boolean; data?: T } | T }>): Promise<T> {
  const r = await p;
  const body = r.data as { data?: T } & T;
  if (body && typeof body === 'object' && 'data' in body && body.data !== undefined) {
    return body.data as T;
  }
  return body as T;
}
